import CDATA from './CDATA';
import config from '../config';
import axios from 'axios';
import Auth0SessionInfo from './Auth0SessionInfo';
import CryptoAES from 'crypto-js/aes';
import CryptoEnc from 'crypto-js/enc-utf8';

const { encKey } = config.sessionStorage;

const getAppState = () => {
  const appStateInSession = sessionStorage.getItem('AppStateStorage');
  return appStateInSession && JSON.parse(
    CryptoAES.decrypt(appStateInSession.toString(), encKey).toString(CryptoEnc),
  );
};

const getClientCode = () => {
  const appState = getAppState();
  return appState?.selectedClient?.code ?? appState.availableClientCodeAndNames[0].code;
};

const getReportingPeriod = () => {
  const appState = getAppState();
  return appState?.selectedReportingDate?.code ?? null;
};

const getSelectedRegulation = () => {
  const appState = getAppState();
  return appState?.selectedRegulation ?? null;
};

const getUserEmail = () => {
  const appState = getAppState();
  return appState?.userEmail ?? 'Unknown';
};

// ─── Narratives ──────────────────────────────────────────────────────

export const getBaseLanguageNarratives = (reportingPeriod, regulation) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_GetBaseLanguageNarratives', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
  }, 'Failed to load narratives');
};

export const getTranslationNarratives = (reportingPeriod, regulation) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_GetTranslationNarrativesBySearch', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
  }, 'Failed to load translation narratives');
};

export const updateBaseNarrativeValue = (narrative) => {
  return CDATA.makeRequest('POST', 'PRIIPS_overview_narrative_update', '', {
    NarrativeId: narrative.narrativeid,
    NarrativeKey: narrative.narrativekey,
    NarrativeValue: narrative.narrativevalue,
    LanguageName: narrative.languagename,
    TranslationStatusName: narrative.translationstatusname,
    ClassCode: narrative.classcode,
    FundCode: narrative.fundcode,
    IsoLanguage: narrative.isolanguage,
    TranslationStatusId: narrative.translationstatusid,
  }, 'Failed to update narrative');
};

export const updateNarrativeTranslationStatusBulk = (narrativeIds, translationStatusId, changedBy) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_UpdateNarrativeTranslationStatus_Bulk', '', {
    ClientCode_bk: clientCode,
    NarrativeIdList: narrativeIds.join(','),
    TranslationStatusId: translationStatusId,
    TranslationStatusChangedBy: changedBy,
  }, 'Failed to update translation status');
};

export const updateBaseNarrativeStatus = (narrativeIds, statusId, email) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_base_narrative_status_update', '', {
    ClientCode_bk: clientCode,
    NarrativeIdList: narrativeIds.join(','),
    TranslationStatusId: statusId,
    TranslationStatusChangedBy: email,
  }, 'Failed to update narrative status');
};

// ─── Funds / Reporting Periods ───────────────────────────────────────

export const getReportingPeriods = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_client_reporting_period', '', null, 'Failed to load reporting periods');

export const getFundsList = (reportingPeriod, regulation, fundCode, shareClassCodes) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_FUND_LIST_V2', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
    FundCode: fundCode || '',
    ShareClassCodes: shareClassCodes || '',
  }, 'Failed to load funds list');
};

export const getFundGroups = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_fund_group', '', null, 'Failed to load fund groups');

// ─── Business Rules Dashboard ────────────────────────────────────────

export const getValidationDashboardGlobal = (reportingPeriod, regulation, searchQuery, fundGroupCode, fundCode) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_ValidationDashboardGlobal', '', {
    ClientCode: clientCode,
    RuleRegulation: regulation,
    ReportingPeriod: reportingPeriod,
    QueryString: searchQuery || null,
    FundGroupCode: fundGroupCode || '',
    FundCode: fundCode || '',
  }, 'Failed to load business rules dashboard');
};

export const getValidationDashboardPerResult = (reportingPeriod, regulation, ruleResult, searchQuery, fundGroupCode, fundCode) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_ValidationDashboardPerResult', '', {
    ClientCode: clientCode,
    RuleRegulation: regulation,
    ReportingPeriod: reportingPeriod,
    RuleResult: ruleResult,
    QueryString: searchQuery || null,
    FundGroupCode: fundGroupCode || '',
    FundCode: fundCode || '',
  }, 'Failed to load business rules per result');
};

export const getValidationDashboardPerField = (reportingPeriod, regulation, ruleResult, ruleField, searchQuery, fundGroupCode, fundCode) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_ValidationDashboardPerResultPerField', '', {
    ClientCode: clientCode,
    RuleRegulation: regulation,
    ReportingPeriod: reportingPeriod,
    RuleResult: ruleResult,
    RuleField: ruleField,
    QueryString: searchQuery || null,
    FundGroupCode: fundGroupCode || '',
    FundCode: fundCode || '',
  }, 'Failed to load business rules per field');
};

export const getRegulations = () =>
  CDATA.makeRequest('GET', 'PRIIPS_doc_regulations', '', null, 'Failed to load regulations',null,null,true);

export const extractBusinessRulesResults = async (fundCode, reportingPeriod, regulation, ruleResult, classCodes, fundGroupCode) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/extract/downloadreport`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    responseType: 'arraybuffer',
    data: {
      ClientCode: clientCode,
      DataFilterColumn: 'ResultType',
      FileType: 'EXCEL',
      Parameters: {
        clientCode,
        reportingPeriod,
        ruleRegulation: regulation,
        ruleResult: ruleResult || '',
        ShareClassCodes: classCodes || '',
        FundCode: fundCode || null,
        FundGroupCode: fundGroupCode || null,
      },
      Reference_Date: reportingPeriod,
      RegulationType: regulation,
      BaseFileName: `${clientCode}_BusinessRules_Output_${reportingPeriod.replaceAll('-', '')}`,
      Procedure: 'ExtractValidationDashboard',
    },
  });

  return response.data;
};

// ─── Templates ───────────────────────────────────────────────────────

export const getTemplates = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_Templates', '', null, 'Failed to load templates');

export const getReportTypes = () =>
  CDATA.makeRequest('GET', 'PRIIPS_doc_reporttypes', '', null, 'Failed to load report types');

export const getFundsAndClasses = () => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('GET', `PRIIPS_app_all_funds_and_classes(${clientCode})`, '', null, 'Failed to load funds and classes');
};

export const getIsoLanguages = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_Get_KID_Languages', '', null, 'Failed to load languages');

export const saveTemplate = async (file, fileName, templateData) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.cdata.url;

  const formData = new FormData();
  if (file) {
    formData.append('TemplateFile', file, fileName);
  }
  formData.append('Data', JSON.stringify(templateData));

  const response = await axios.post(
    `${baseURL}/SaveTemplate`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        authorization: `Bearer ${token}`,
        client_code: clientCode,
      },
      timeout: 900000,
    },
  );

  return response.data;
};

export const deleteTemplate = async (templateId) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.cdata.url;

  const response = await axios.post(
    `${baseURL}/DeleteTemplate`,
    { TemplateId: templateId },
    {
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
        client_code: clientCode,
      },
      timeout: 900000,
    },
  );

  return response.data;
};

// ─── Translation ─────────────────────────────────────────────────────

export const getTranslationStatuses = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_TranslationStatus', '', null, 'Failed to load translation statuses');

export const getTranslationOverview = (reportingPeriod, regulation, fundCode, shareClassCodes) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_Translation_Overview', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
    FundCode: fundCode || '',
    ShareClassCodes: shareClassCodes || '',
  }, 'Failed to load translation overview');
};

export const importTranslationXml = async (file, fileName) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const formData = new FormData();
  formData.append('file', file, fileName);

  const response = await axios.post(
    `${baseURL}/translation/ImportArcXmlFO`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        authorization: `Bearer ${token}`,
        client_code: clientCode,
      },
      timeout: 900000,
    },
  );
  return response.data;
};

export const exportTranslationXml = async (exportContract) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/translation/ExportArcXml`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    responseType: 'arraybuffer',
    data: exportContract,
  });
  return response.data;
};

export const generateReport = async (contract) => {
  return CDATA.makeRequest('POST', 'PRIIPS_app_GenerateReport', '', contract, 'Failed to generate report');
};

const REPORT_GENERATE_PROCEDURE = {
  EMTV41: 'app.Create_EMTv41_Bulk',
  EMTV42: 'app.Create_EMTv42_Bulk',
  EMTV43: 'app.Create_EMTv43_Bulk',
  'EPTV2.1': 'app.Create_EPTv21_Bulk',
};

const resolveGenerateProcedure = (reportTypeName) => {
  const normalized = (reportTypeName || '').toUpperCase();
  return REPORT_GENERATE_PROCEDURE[normalized] || null;
};

export const generateReportsDurable = async ({
  reportingPeriod,
  regulation,
  reportTypeName,
  classCodes,
  fundCodes,
  userEmail,
}) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const extractBaseURL = `${config.apim.url}/extract`;
  const procedure = resolveGenerateProcedure(reportTypeName);

  if (!procedure) {
    throw new Error(`Overview generate is not supported for ${reportTypeName}`);
  }

  const API = axios.create({
    baseURL: `${extractBaseURL}/transaction`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const payload = {
    procedure,
    clientcode: clientCode,
    RegulationType: regulation,
    parameters: {
      Reference_Date: reportingPeriod,
      FundCodes: (fundCodes || []).join(','),
      ClassCodes: (classCodes || []).join(','),
      ClientCode_bk: clientCode,
      UserEmail: userEmail,
      RegulationType: regulation,
    },
  };

  const response = await API.request('', { method: 'POST', data: payload });
  return response.data;
};

export const getDocumentStatus = async (contract) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/reportdata/DocumentStatus`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', { method: 'POST', data: contract });
  return response.data;
};

export const downloadArtifacts = async (contract) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/artifactdownload/Download`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    responseType: 'arraybuffer',
    data: contract,
  });
  return response.data;
};

export const approveReports = async (reportsToProcess) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const reportingPeriod = getReportingPeriod();
  const baseURL = config.cdata.url;

  const API = axios.create({
    baseURL: `${baseURL}Set_DocumentArtifact_ApprovalStatus_Bulk`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    data: {
      ReportsToProcess: reportsToProcess,
      ClientCode: clientCode,
      ReportingPeriod: reportingPeriod,
    },
  });
  return response.data;
};

const REPORT_APPROVE_REOPEN_PROCEDURE = {
  EMTV41: 'app.ApproveandReOpen_EMTv41_Bulk',
  EMTV42: 'app.ApproveandReOpen_EMTv42_Bulk',
  EMTV43: 'app.ApproveandReOpen_EMTv43_Bulk',
};

const resolveApproveReopenProcedure = (reportTypeName) => {
  const normalized = (reportTypeName || '').toUpperCase();
  return REPORT_APPROVE_REOPEN_PROCEDURE[normalized] || null;
};

export const approveOrReopenReportsDurable = async ({
  reportingPeriod,
  regulation,
  reportTypeName,
  action,
  classCodes,
  fundCodes,
  userEmail,
}) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const extractBaseURL = `${config.apim.url}/extract`;
  const procedure = resolveApproveReopenProcedure(reportTypeName);

  if (!procedure) {
    throw new Error(`Overview approve/reopen is not supported for ${reportTypeName}`);
  }

  const API = axios.create({
    baseURL: `${extractBaseURL}/transaction`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const payload = {
    procedure,
    clientcode: clientCode,
    RegulationType: regulation,
    parameters: {
      Reference_Date: reportingPeriod,
      Fund_Codes: (fundCodes || []).join(','),
      Class_Codes: (classCodes || []).join(','),
      ClientCode_bk: clientCode,
      UserName: userEmail,
      Action: action,
    },
  };

  const response = await API.request('', { method: 'POST', data: payload });
  return response.data;
};

// Maps report TypeName to the CDATA procedure suffix used for bulk approve/reopen.
// Only map names that differ from their procedure suffix; all others pass through directly.
const REPORT_TYPE_APPROVE_LEVEL = {
  'EPTV2.1': 'EPT',
  EPTV2: 'EPT',
  EPT: 'EPT',
  'EMTV4.3': 'EMTv43',
  EMTV4: 'EMTv4',
  EMTV3: 'EMTv3',
  EMTV31: 'EMTv3',
  EMT: 'EMT',
};

const resolveReportLevel = (reportTypeName) => {
  const normalized = (reportTypeName || '').toUpperCase();
  return REPORT_TYPE_APPROVE_LEVEL[normalized] || reportTypeName;
};

/**
 * Approve EMT/EPT reports via the CDATA bulk approve procedure.
 * This is the correct path for non-KID report types (EMT/EPT all versions).
 * Mirrors the Blazor editor pattern: PRIIPS_app_APPROVE_{level}_BULK
 * @param {string[]} recordIds - List of AutoID/RECORD_ID values from reportTypeStatuses
 * @param {string} reportTypeName - The TypeName of the report (e.g. "EMTv41", "EPTV2.1")
 */
export const approveReportsByLevel = async (recordIds, reportTypeName) => {
  const clientCode = getClientCode();
  const level = resolveReportLevel(reportTypeName);

  return CDATA.makeRequest(
    'POST',
    `PRIIPS_app_APPROVE_${level}_BULK`,
    '',
    {
      ClientCode_bk: clientCode,
      RecordIDList: recordIds.join(','),
      UserName: getUserEmail()
    },
    `Failed to approve ${reportTypeName} reports`,
    null,
    null,
    true
  );
};

/**
 * Reopen EMT/EPT reports via the CDATA bulk reopen procedure.
 * @param {string[]} recordIds - List of AutoID/RECORD_ID values
 * @param {string} reportTypeName - The TypeName of the report
 */
export const reopenReportsByLevel = async (recordIds, reportTypeName) => {
  const clientCode = getClientCode();
  const level = resolveReportLevel(reportTypeName);

  return CDATA.makeRequest(
    'POST',
    `PRIIPS_app_REOPEN_${level}_BULK`,
    '',
    {
      ClientCode_bk: clientCode,
      RecordIDList: recordIds.join(','),
      UserName: getUserEmail()
    },
    `Failed to reopen ${reportTypeName} reports`,
    null,
    null,
    true
  );
};

export const reopenReport = async (classCode, fundCode, reportTypeId) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const reportingPeriod = getReportingPeriod();
  const baseURL = config.cdata.url;

  const API = axios.create({
    baseURL: `${baseURL}Set_DocumentArtifact_ApprovalStatus_Bulk`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    data: {
      ReportsToProcess: [{
        FundCode: fundCode,
        ReportTypeId: reportTypeId,
        ClassCodes: [classCode],
        ActionType: 'R',
      }],
      ClientCode: clientCode,
      ReportingPeriod: reportingPeriod,
    },
  });
  return response.data;
};

export const exportTranslationToExcel = async (reportingPeriod, regulation, fundCode, shareClassCodes) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/extract/downloadreport`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    responseType: 'arraybuffer',
    data: {
      ClientCode: clientCode,
      FileType: 'EXCEL',
      Parameters: {
        clientCode,
        reportingPeriod,
        ruleRegulation: regulation,
        FundCode: fundCode || '',
        ShareClassCodes: shareClassCodes || '',
      },
      Reference_Date: reportingPeriod,
      RegulationType: regulation,
      BaseFileName: `${clientCode}_Translations_${reportingPeriod.replaceAll('-', '')}`,
      Procedure: 'ExtractTranslations',
    },
  });

  return response.data;
};

// ─── Overview V2 ───────────────────────────────────────────────────────

export const getFundsOverview = (reportingPeriod, regulation, fundCode, shareClassCodes, fundGroupCode) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_fund_overview_v2', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
    FundCode: fundCode || '',
    ShareClassCodes: shareClassCodes || '',
    FundGroupCode: fundGroupCode || '',
  }, 'Failed to load funds overview');
};

export const getFundClassesOverview = (reportingPeriod, regulation, fundCode, shareClassCodes) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_app_fundclass_overview_v2', '', {
    ReportingPeriod: reportingPeriod,
    RuleRegulation: regulation,
    ClientCode: clientCode,
    FundCode: fundCode,
    ShareClassCodes: shareClassCodes || '',
  }, 'Failed to load fund classes overview');
};

export const calculateRiskAndPerf = async ({
  reportingPeriod,
  regulation,
  userAzureId,
  userEmail,
  calculateForAllShareClasses,
  overviewData,
  fundCards,
}) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const extractBaseURL = `${config.apim.url}/extract`;

  const cards = fundCards || [];

  // Keep parity with Blazor's GetClassCodesList/GetFundsCodesList behavior.
  const classCodes = cards
    .flatMap((card) => {
      const gridData = card.gridData || [];
      if (gridData.length === 0) return [];

      return calculateForAllShareClasses
        ? gridData.map((cl) => cl.classCode)
        : gridData.filter((cl) => cl.isSelected).map((cl) => cl.classCode);
    })
    .filter(Boolean)
    .join(',');

  let fundCodesList = calculateForAllShareClasses
    ? cards.map((card) => card.fundCode)
    : cards.filter((card) => card.isSelected).map((card) => card.fundCode);

  // Remove funds where not all classes are currently available.
  const fundsToRemove = new Set(
    cards
      .filter((card) => (card.classCount || 0) > (card.shownClassCount || 0))
      .map((card) => card.fundCode)
  );
  fundCodesList = fundCodesList.filter((fundCode) => !fundsToRemove.has(fundCode));

  const fundCodes = fundCodesList.filter(Boolean).join(',');

  const API = axios.create({
    baseURL: `${extractBaseURL}/calculateriskandperf`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const body = {
    Procedure: 'Risk_And_Performance_AssetManager_Final_Update_RTS2',
    ClientCode: clientCode,
    RegulationType: regulation,
    Parameters: {
      AzureID: userAzureId,
      CalcDate: reportingPeriod,
      Fund_Codes: fundCodes,
      Class_Codes: classCodes,
      Class_Name: '',
      Client_Code: clientCode,
      Entity: 'Risk and Performance Calculation (New UI)',
      UPDATE: true,
      USER: userEmail,
    },
  };

  const response = await API.request('', { method: 'POST', data: body });
  return response.data;
};

export const runBusinessRules = async ({
  reportingPeriod,
  regulation,
  userAzureId,
  userEmail,
}) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const extractBaseURL = `${config.apim.url}/extract`;

  const API = axios.create({
    baseURL: `${extractBaseURL}/generateextract`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const procedures = regulation === 'MiFID'
    ? ['Rule_Regulation_Population_EMTv41', 'Rule_Regulation_Population_EMTv42', 'Rule_Regulation_Population_EMTv43', 'Nav_Rule_Regulation_Population']
    : ['Rule_Regulation_Population_EPTv21', 'Nav_Rule_Regulation_Population'];

  const calls = procedures.map((procedure) => API.request('', {
    method: 'POST',
    data: {
      procedure,
      clientcode: clientCode,
      RegulationType: regulation,
      parameters: {
        Client_Code: clientCode,
        CalcDate: reportingPeriod,
        RegulationType: regulation,
        RuleRegulation: regulation,
        AzureID: userAzureId,
        USER: userEmail,
      },
    },
  }));

  const responses = await Promise.all(calls);
  return responses.map((r) => r.data);
};

export const extractResults = async ({
  reportingPeriod,
  regulation,
  actionLabel,
  extractAll,
  overviewData,
  fundCards,
}) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const extractBaseURL = `${config.apim.url}/extract`;

  const selectedItems = extractAll
    ? overviewData
    : (overviewData || []).filter((x) => x.IsSelected);

  const classCodes = [...new Set((selectedItems || []).map((x) => x.ClassCode).filter(Boolean))].join(',');

  // Extract Fund_Codes with same logic as Blazor's GetFundsCodesList
  const cards = fundCards || [];
  let fundCodesList = extractAll
    ? cards.map((card) => card.fundCode)
    : cards.filter((card) => card.isSelected).map((card) => card.fundCode);
  const fundsToRemove = new Set(
    cards
      .filter((card) => (card.classCount || 0) > (card.shownClassCount || 0))
      .map((card) => card.fundCode)
  );
  fundCodesList = fundCodesList.filter((fundCode) => !fundsToRemove.has(fundCode));
  const fundCodes = fundCodesList.filter(Boolean).join(',');

  const payload = {
    BaseFileName: '',
    ClientCode: clientCode,
    ColumnFilters: null,
    DataFilterColumn: 'ResultType',
    FileType: 'CSV',
    Parameters: {
      Client_Code: clientCode,
      Fund_Codes: fundCodes,
      Class_Codes: classCodes,
      CalcDate: reportingPeriod,
    },
    AdditionalParameters: null,
    Procedure: '',
    RegulationType: regulation,
    Reference_Date: reportingPeriod,
    Version: 'v2rp',
    Delimiter: '|',
    DateFormatString: null,
  };

  if (actionLabel === 'R&P Results') {
    payload.Procedure = 'Risk_And_Performance_Extract_V2';
    payload.BaseFileName = 'RP_Output__1_RP_results';
    payload.ColumnFilters = {
      RPResults: 'EXTRACT_RP_1_Results',
      ExtendedRPResults: '',
      AnnualReturns: '',
    };
  } else if (actionLabel === 'Extended R&P Results') {
    payload.Procedure = 'Risk_And_Performance_Extended_Extract_V2';
    payload.BaseFileName = 'RP_Output__2_Extended_RP_results';
    payload.ColumnFilters = {
      RPResults: '',
      ExtendedRPResults: 'EXTRACT_RP_2_Extended Results',
      AnnualReturns: '',
    };
  } else if (actionLabel === 'Annual Returns') {
    payload.Procedure = 'Risk_And_Performance_Annual_Returns_Extract_V2';
    payload.BaseFileName = 'RP_Output__3_Annual_returns';
    payload.ColumnFilters = {
      RPResults: '',
      ExtendedRPResults: '',
      AnnualReturns: 'EXTRACT_RP_3_Annual Returns',
    };
  } else if (actionLabel === 'Published R&P Results') {
    payload.Procedure = 'Risk_And_Performance_Published_Extract_V2';
    payload.BaseFileName = 'Published_RP_Output__2_RP_results';
    payload.ColumnFilters = null;
  } else {
    payload.Procedure = 'Risk_And_Performance_Published_Extended_Extract_V2';
    payload.BaseFileName = 'Extended_Published_RP_Output__2_RP_results';
    payload.ColumnFilters = null;
  }

  const API = axios.create({
    baseURL: `${extractBaseURL}/generatereport`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', { method: 'POST', data: payload });
  return response.data;
};
